import {
  Fragment,
} from 'react';
import Text from '@/ui/components/Text';

import styles from './page.module.css';

const TEXT_BLOCKS = [
  {
    title: '1. Introduction',
    subtitle: 'Welcome to Dinosave! Your privacy is very important to us. This Privacy Policy \
               explains how Dinosave ("we", "us", or "our") collects, uses, and protects your \
               personal information when you use it. By using Dinosave, you agree to the collection \
               and use of your information in accordance with this policy.',
  },
  {
    title: '2. Active Data Collection',
    subtitle: 'This refers to the data you voluntarily provide.',
    text: [
      'Account information: your name, email address, username, \
      password (hashed) and any profile details you choose to provide',
      'Wishlists and items: wishlists you create and items you add (name, price, link, image, etc.)',
      'Communication: feedback, support requests, or other communications you send to us.',
    ],
    note: 'We may receive information about you from third parties, such as social network integrations \
           (if you choose to log in via Apple, Google, etc.), analytics providers, or other partners.',
  },
  {
    title: '3. Automatic Data Collection',
    subtitle: 'This refers to the data Dinosave automatically collects from your devices.',
    text: [
      'Usage data: information about how you use Dinosave (features used, time spent, click-throughs, etc.)',
      'Device and log information: device type, operating system, unique device identifiers, IP address and log files.',
    ],
  },
  {
    title: '4. How We Use Your Information',
    subtitle: 'We use your information for the following purposes:',
    text: [
      'For analysis to improve Dinosave.',
      'To interact and communicate with you.',
      'To provide and maintain Dinosave features.',
    ],
  },
  {
    title: '5. How We Share Your Information',
    subtitle: 'We may share your information in the following circumstances:',
    text: [
      'With service providers who perform services on our behalf (hosting, analytics, \
      email delivery, payment processing). These providers are contractually obligated \
      to keep your data secure and use it only on our behalf.',
      'With your consent or at your direction (for example, when you share a wishlist with friends or family).',
      'For legal reasons: if required by law, regulation, court order, or to respond to government requests.',
      'Business transfers: if we merge, acquire or sell assets, your information may be \
      transferred as part of that transaction (with appropriate confidentiality protections).',
    ],
  },
  {
    title: '6. Data Retention',
    subtitle: 'We will retain your personal information only as long as necessary to fulfill the \
    purposes set out in this policy (to maintain your account, comply with legal obligations, or resolve disputes). \
    When we no longer need your information, we will delete it, unless retention is required by law.',
  },
  {
    title: '7. Your Rights',
    subtitle: 'Depending on your jurisdiction, you may have the following rights:',
    text: [
      'Access: request a copy of the personal information we hold about you.',
      'Rectification: ask us to correct inaccurate or incomplete information.',
      'Deletion: request that we delete your personal information (subject to legal exceptions).',
      'Restriction or objection: request that we restrict or object to certain types of processing.',
      'Data portability: request a copy of your personal information in machine-readable format.',
      <Fragment key="contact">
        Withdraw consent: since we rely on your consent to process information, you may&nbsp;
        withdraw it at any time (this will not affect processing done prior to withdrawal).&nbsp;
        To exercise any of these rights, please contact us at&nbsp;
        <a href="mailto:feedback@dinosave.com">
          <Text weight="500">
            feedback@dinosave.com
          </Text>
        </a>
      </Fragment>,
    ],
  },
  {
    title: '8. Security',
    subtitle: 'We take reasonable steps to protect your personal information from unauthorized access, \
               disclosure, alteration or destruction. We implement industry-standard security measures \
               (encryption, secure servers, access controls). However, no method of transmission or storage \
               is 100% secure, so we cannot guarantee absolute security.',
  },
  {
    title: '9. Children’s Privacy',
    subtitle: 'Dinosave is not intended for children under the age of 13 (or higher age depending on your \
               jurisdiction). We do not knowingly collect personal information from children under that age. \
               If you are a parent or guardian and believe your child has provided us with personal \
               information, please contact us, and we will delete that information.',
  },
  {
    title: '10. International Transfers',
    subtitle: 'Your information may be processed or stored in any country where we or our service \
               providers operate. If you are located in jurisdictions (e.g., EU/EEA, UK) where data \
               protection laws require specific safeguards for international transfers, we will implement \
               appropriate measures to protect your data.',
  },
  {
    title: '11. Cookies',
    subtitle: 'We currently refrain from using cookies.',
  },

  {
    title: '12. Limitations',
    subtitle: 'Dinosave may contain links to third-party websites, services or social media platforms \
               that are not owned or controlled by Dinosave. We are not responsible for the privacy practices \
               or content of those third-parties. Please review their privacy policies before providing any \
               personal information.',
  },
  {
    title: '13. Policy Updates',
    subtitle: 'We may update this Privacy Policy from time to time. When we do, we will revise the \
               “Last updated” date at the top. We encourage you to review this policy periodically. \
               If we make material changes, we may provide you with additional notice.',
  },
  {
    title: '14. Contact',
    subtitle: (
      <Fragment key="contact">
        If you have questions or concerns about this Privacy Policy or our data practices, please contact us at&nbsp;
        <a href="mailto:feedback@dinosave.com">
          <Text weight="500">
            feedback@dinosave.com
          </Text>
        </a>
      </Fragment>
    ),
  },
];

function PrivacyPage() {
  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={styles.privacyPage}>
      <Text
        kind="super"
        className={styles.title}
      >
        Privacy Policy
      </Text>

      <Text
        kind="paragraph"
        size="large"
        weight="300"
        className={styles.subtitle}
      >
        Last updated May 3, 2026
      </Text>

      <div className={styles.disclaimer}>
        <Text
          kind="paragraph"
          size="regular"
        >
          This policy is effective from Nov 7, 2025.
          If you have any concerns or questions regarding your privacy, feel free to contact us at:
          <Text weight="500">
            <a href="mailto:feedback@dinosave.com">
              &nbsp;feedback@dinosave.com
            </a>
          </Text>
        </Text>
      </div>

      {TEXT_BLOCKS.map((block, index) => (
        <div
          key={index}
          className={styles.block}
        >
          <Text
            kind="heading-4"
            weight="600"
            className={styles.blockTitle}
          >
            {block.title}
          </Text>

          <ul className={styles.blockText}>
            {block.subtitle && (
              <Text
                kind="paragraph"
                size="regular"
                weight="300"
                className={styles.blockTextSubtitle}
              >
                {block.subtitle}
              </Text>
            )}
            {block.text?.map((text, index) => (
              <li
                key={index}
                className={styles.listItem}
              >
                <Text
                  kind="paragraph"
                  size="regular"
                  weight="300"
                >
                  {text}
                </Text>
              </li>
            ))}
            {block.note && (
              <Text
                kind="paragraph"
                size="regular"
                weight="300"
                className={styles.blockTextNote}
              >
                {block.note}
              </Text>
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default PrivacyPage;
